import { useState } from 'react';
import { AgxToastState } from 'types/commonTypes';
import {
  CampaignDetailModel,
  StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus,
  UploadedDocument,
  useInvalidateUseStaffGetBlankContractDetailsForCampaign,
  useInvalidateUseStaffGetCampaign,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useStaffGetTenancyInformationForCampaign,
  useStaffUpdateBlankContractDetailsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { ComplianceData, isTenancyAgreementRequired } from './contractCompliance';

interface UseContractModalStateArgs {
  campaign?: CampaignDetailModel | undefined;
  updateToastState: (toastState: AgxToastState) => void;
}

interface ContractModalState {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  showTenancyAgreement: boolean;
  handleModalComplete: (payload: {
    complianceData: ComplianceData;
    uploadedDocument: UploadedDocument;
    isFinalSelected: boolean;
  }) => Promise<void>;
}

const useContractModalState = ({
  campaign,
  updateToastState,
}: UseContractModalStateArgs): ContractModalState => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: tenancyInformationResponse } = useStaffGetTenancyInformationForCampaign(
    campaign?.campaignId ?? '',
    !!campaign?.campaignId
  );

  const invalidateStaffGetBlankContractDetails =
    useInvalidateUseStaffGetBlankContractDetailsForCampaign();
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign();
  const invalidateStaffGetCampaign = useInvalidateUseStaffGetCampaign();

  const { staffUpdateBlankContractDetailsForCampaign } =
    useStaffUpdateBlankContractDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    });

  const showTenancyAgreement = isTenancyAgreementRequired(
    tenancyInformationResponse?.tenantedPropertyDetails?.tenancyType
  );

  const handleModalComplete = async ({
    uploadedDocument,
    isFinalSelected,
  }: {
    complianceData: ComplianceData;
    uploadedDocument: UploadedDocument;
    isFinalSelected: boolean;
  }) => {
    if (!campaign?.campaignId) return;

    // React Compiler cannot lower a conditional inside try/catch; keep it outside.
    const blankSalesContractStatus = isFinalSelected
      ? StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Final
      : StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Draft;

    try {
      await staffUpdateBlankContractDetailsForCampaign({
        campaignId: campaign.campaignId,
        body: { blankSalesContract: uploadedDocument, blankSalesContractStatus },
      });
      void Promise.all([
        invalidateStaffGetBlankContractDetails(campaign.campaignId),
        invalidateStaffGetPropertyInformationProgress(campaign.campaignId),
        // The save can change blankContractIsLive, which the campaign carries.
        invalidateStaffGetCampaign(campaign.campaignId),
      ]).catch(() => undefined);
      updateToastState({
        color: 'success',
        message: 'Contract saved and sent to REX',
        open: true,
      });
    } catch {
      updateToastState({
        color: 'error',
        message: 'Error saving contract',
        open: true,
      });
    }
  };

  return {
    isModalOpen,
    setIsModalOpen,
    showTenancyAgreement,
    handleModalComplete,
  };
};

export default useContractModalState;
